import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Icon } from '../../components/Icon'
import { Logo } from '../../components/ui'
import { LEGAL_LINKS } from '../../lib/company'
import { AppWindowMock, ChatCard, HeroVideo, ProfileMock, WebsiteStart } from './parts'
import { ChannelsSection, ControlSection, CustomerFlow, DashboardSection, Faq, FinalCta, Jobs, LearningSection, Pricing, SetupSteps, SpeedWins } from './sections'

const NAV = [
  { href: '#channels', label: 'Channels' },
  { href: '#how', label: 'How it works' },
  { href: '#what', label: 'What it does' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#faq', label: 'FAQ' },
]

function Nav() {
  const [open, setOpen] = useState(false)
  // Transparent over the hero glow; paper background once the page scrolls under it.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return (
    <header
      className={`sticky top-0 z-30 border-b transition-colors ${
        scrolled || open ? 'border-border bg-bg/90 backdrop-blur supports-[backdrop-filter]:bg-bg/75' : 'border-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3.5 sm:px-10 lg:px-[120px] lg:py-5">
        <Logo />
        <nav aria-label="Main" className="hidden items-center gap-9 lg:flex">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="text-[14px] font-medium hover:text-accent">
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3.5 lg:gap-6">
          <Link to="/login" className="text-[14px] font-medium hover:text-accent">
            Login
          </Link>
          <Link to="/login" className="btn btn-primary !px-[13px] !py-[9px] !text-[14px] lg:!px-4 lg:!py-2.5 lg:!text-[15px]">
            Sign up for Free
          </Link>
          <button
            type="button"
            className="hidden rounded-md p-1.5 text-ink sm:inline-flex lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((o) => !o)}
          >
            <Icon name={open ? 'x' : 'menu'} size={18} />
          </button>
        </div>
      </div>
      {open && (
        <nav id="mobile-nav" aria-label="Main" className="border-t border-border px-5 pb-4 lg:hidden">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block border-b border-border py-3 text-[15px] font-medium">
              {n.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  )
}

/** Soft colour blobs and frosted tiles behind the nav and hero ("Hero glow" in Lobby.pen), fading into the page. */
function HeroGlow() {
  const blob = 'absolute rounded-full'
  const tile = 'absolute origin-top-left border border-white/55 bg-white/[0.18] backdrop-blur-[12px]'
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[760px] overflow-hidden lg:h-[860px]">
      {/* Mobile (390 wide in the design) */}
      <div className="absolute left-1/2 top-0 h-full w-[390px] -translate-x-1/2 lg:hidden">
        <div className={`${blob} -left-[140px] -top-[140px] h-[300px] w-[380px] bg-[#9FD9B8B3] blur-[35px]`} />
        <div className={`${blob} -top-[180px] left-[120px] h-[240px] w-[300px] bg-[#0E7A4959] blur-[35px]`} />
        <div className={`${blob} -top-[60px] left-[200px] h-[260px] w-[320px] bg-[#F6C9A8B3] blur-[35px]`} />
        <div className={`${tile} -left-[30px] top-[90px] h-[90px] w-[90px] -rotate-12 rounded-[14.4px]`} />
        <div className={`${tile} left-[310px] top-[60px] h-[100px] w-[100px] rotate-10 rounded-[16px]`} />
      </div>
      {/* Desktop (1440 wide in the design) */}
      <div className="absolute left-1/2 top-0 hidden h-full w-[1440px] -translate-x-1/2 lg:block">
        <div className={`${blob} -left-[160px] -top-[220px] h-[520px] w-[760px] bg-[#9FD9B8B3] blur-[60px]`} />
        <div className={`${blob} -top-[300px] left-[420px] h-[440px] w-[640px] bg-[#0E7A4959] blur-[60px]`} />
        <div className={`${blob} -top-[200px] left-[880px] h-[500px] w-[720px] bg-[#F6C9A8B3] blur-[60px]`} />
        <div className={`${blob} left-[1120px] top-[160px] h-[340px] w-[480px] bg-[#BFE6CE80] blur-[60px]`} />
        <div className={`${blob} -left-[80px] top-[260px] h-[300px] w-[420px] bg-[#F6DCC480] blur-[60px]`} />
        <div className={`${tile} left-[150px] top-[170px] h-[150px] w-[150px] -rotate-12 rounded-[24px]`} />
        <div className={`${tile} left-[1150px] top-[120px] h-[170px] w-[170px] rotate-10 rounded-[27.2px]`} />
        <div className={`${tile} left-[250px] top-[470px] h-[110px] w-[110px] rotate-14 rounded-[17.6px]`} />
        <div className={`${tile} left-[1080px] top-[430px] h-[120px] w-[120px] -rotate-8 rounded-[19.2px]`} />
      </div>
      <div className="absolute inset-x-0 bottom-0 h-[418px] bg-gradient-to-b from-bg/0 to-bg lg:h-[473px]" />
    </div>
  )
}

function Hero() {
  return (
    <section className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-[22px] px-5 pb-10 pt-8 text-center sm:px-10 lg:gap-7 lg:px-[120px] lg:pb-16 lg:pt-14">
      <h1 className="flex flex-col items-center text-ink">
        <span className="h-sans text-[42px] leading-[43px] sm:text-[60px] sm:leading-[62px] lg:text-[76px] lg:leading-[78px]">
          Answer every DM
        </span>
        <span className="flex flex-col items-center sm:flex-row sm:items-end sm:gap-[18px]">
          <span className="h-sans text-[42px] leading-[43px] sm:text-[60px] sm:leading-[62px] lg:text-[76px] lg:leading-[78px]">
            before they
          </span>
          <span className="h-serif text-[46px] leading-[47px] sm:text-[66px] sm:leading-[68px] lg:text-[84px] lg:leading-[86px]">
            book elsewhere.
          </span>
        </span>
      </h1>
      <p className="max-w-[680px] text-[16px] leading-[1.55] text-muted lg:text-[18px]">
        Lobby answers your salon’s Instagram and Facebook messages in seconds, replies to “Price?” comments, books the appointment and
        follows up. You see every chat and can take over from your phone at any time.
      </p>
      <div className="flex w-full flex-col items-center gap-2.5 lg:gap-3">
        <WebsiteStart variant="hero" />
        <p className="whitespace-pre text-[13px] font-medium text-muted">Free trial  ·  Live in 15 minutes  ·  Cancel anytime</p>
      </div>
      <ul className="flex flex-col items-center gap-2.5 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-7">
        {(
          [
            ['zap', 'Replies in seconds, day and night'],
            ['messages-square', 'On Instagram and Facebook'],
            ['hand', 'You can take over any chat'],
          ] as const
        ).map(([icon, text]) => (
          <li key={text} className="flex items-center gap-2 text-[14px] font-medium">
            <Icon name={icon} size={16} className="text-accent" />
            {text}
          </li>
        ))}
      </ul>
      <HeroVideo />
    </section>
  )
}

/** Mobile only: photo with the WhatsApp chat card ("Hero visual" in the mobile frame; removed on desktop). */
function HeroVisual() {
  return (
    <div className="relative h-[620px] w-full overflow-hidden bg-sand lg:hidden">
      <img
        src="/hero-bg-2.jpg"
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
        width={1475}
        height={1800}
      />
      <div className="absolute inset-0 bg-ink/20" aria-hidden />
      <ChatCard className="absolute left-5 right-5 top-6 sm:left-10 sm:right-auto sm:w-[350px]" />
    </div>
  )
}

const FOOTER_COLUMNS: { title: string; links: { label: string; href: string; route?: boolean }[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Channels', href: '#channels' },
      { label: 'How it works', href: '#how' },
      { label: 'What it does', href: '#what' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'FAQ', href: '#faq' },
    ],
  },
  {
    title: 'Get started',
    links: [
      { label: 'Start free trial', href: '/login', route: true },
      { label: 'Sign in', href: '/login', route: true },
    ],
  },
  {
    title: 'Legal',
    links: LEGAL_LINKS.map((l) => ({ ...l, route: true })),
  },
]

/** EU flag: twelve gold stars on blue. */
function EuFlag() {
  const stars = Array.from({ length: 12 }, (_, i) => {
    const a = (i * Math.PI) / 6
    return { x: 12 + 6 * Math.sin(a), y: 8 - 6 * Math.cos(a) }
  })
  return (
    <svg width="21" height="14" viewBox="0 0 24 16" aria-hidden className="shrink-0 rounded-[2px]">
      <rect width="24" height="16" fill="#003399" />
      {stars.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="0.85" fill="#FFCC00" />
      ))}
    </svg>
  )
}

function FooterLink({ label, href, route }: { label: string; href: string; route?: boolean }) {
  const cls = 'group inline-flex items-center gap-1.5 text-[15px] text-muted hover:text-ink'
  const content = (
    <>
      {label}
      <Icon name="arrow-right" size={13} className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
    </>
  )
  return route ? (
    <Link to={href} className={cls}>
      {content}
    </Link>
  ) : (
    <a href={href} className={cls}>
      {content}
    </a>
  )
}

function Footer() {
  return (
    <footer className="border-t border-border">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-12 px-5 pb-8 pt-14 sm:px-10 lg:px-[120px] lg:pb-10 lg:pt-20">
        <div className="flex flex-col gap-10 lg:flex-row lg:justify-between">
          <div className="flex max-w-[320px] flex-col gap-4">
            <Logo />
            <p className="text-[16px] leading-[24px]">Answers every new client within seconds, day and night. You stay in charge of every chat.</p>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:gap-x-20">
            {FOOTER_COLUMNS.map((col) => (
              <nav key={col.title} aria-label={col.title} className="flex flex-col gap-3.5">
                <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted">{col.title}</h2>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <FooterLink {...l} />
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>
        <div className="flex flex-col-reverse gap-4 border-t border-border pt-6 text-[13px] text-muted sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} Lobby
          </p>
          <p className="inline-flex items-center gap-2 font-medium text-ink">
            <EuFlag />
            Made and hosted in the EU
          </p>
        </div>
      </div>
    </footer>
  )
}

export function Landing() {
  return (
    <div className="relative isolate min-h-screen overflow-x-hidden bg-bg">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      <HeroGlow />
      <Nav />
      <main id="main">
        <Hero />
        <HeroVisual />
        <SpeedWins />
        <ChannelsSection />
        <CustomerFlow />
        <Jobs />
        <SetupSteps />
        <DashboardSection>
          <AppWindowMock url="lobbyreply.com/app/profile" activeLabel="Business profile">
            <ProfileMock />
          </AppWindowMock>
        </DashboardSection>
        <ControlSection />
        <LearningSection />
        <Pricing />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </div>
  )
}
